import {
    AuthPage,
    BimDto,
    CfeConnectPage,
    CfeDiagnosticPage,
    CfeInventoryPage,
    ContractType,
    InvoicingLevel,
    MatchingLogic,
    Page,
    ResourceAction,
    Service,
    SettingsPage,
    UploadsPage,
    validateStrictDecoder,
} from "@flexidao/dto";
import { NonEmptyArray } from "@flexidao/helpers";
import * as D from "schemawax";
import { isoTimestampDecoder } from "../utils";
import {
    contractEnergySourceDecoder,
    contractTypeDecoder,
    dataAccessDecoder,
    energySourceDecoder,
    siteTypeDecoder,
} from "./misc";

const tenantStatusDecoder = D.literalUnion(
    BimDto.TenantStatus.Approved,
    BimDto.TenantStatus.Cancelled,
    BimDto.TenantStatus.Pending,
    BimDto.TenantStatus.Rejected,
);

export const tenantDecoder: D.Decoder<BimDto.Tenant> = D.recursive(() =>
    D.object({
        required: {
            tenantId: D.string,
            name: D.string,
            description: D.string,
            status: tenantStatusDecoder,
            contactEmail: D.string,
        },
        optional: {
            users: D.array(userDecoder),
            services: D.oneOf(D.array(flexidaoServiceDecoder), D.array(D.string)),
            procurementAccountId: D.nullable(D.string),
        },
    }),
);

const resourceActionDecoder = D.literalUnion(
    ResourceAction.Read,
    ResourceAction.Upsert,
    ResourceAction.Delete,
);

export const serviceIdDecoder: D.Decoder<Service> = D.string.andThen((s) => {
    if (Object.values(Service).includes(s as Service)) {
        return s as Service;
    }
    throw new Error(
        `Invalid service id '${s}'. Make sure this is defined in both DTO enum, in the webserver, and in the openapi schema from bim.`,
    );
});

export const flexidaoServiceDecoder: D.Decoder<BimDto.FlexidaoService> = D.recursive(() =>
    D.object({
        required: {
            flexidaoServiceId: serviceIdDecoder,
            name: D.string,
            description: D.string,
        },
        optional: {
            tenants: D.array(tenantDecoder),
        },
    }),
);

export const roleDecoder: D.Decoder<BimDto.Role> = D.recursive(() =>
    D.object({
        required: {
            roleId: D.string,
            name: D.string,
            tenant: D.oneOf(tenantDecoder, D.string),
        },
        optional: {
            users: D.array(userDecoder),
            permissions: D.array(D.oneOf(permissionDecoder, D.string)),
        },
    }),
);

export const permissionDecoder: D.Decoder<BimDto.Permission> = D.object({
    required: {
        permissionId: D.string,
        flexidaoService: D.oneOf(serviceIdDecoder, flexidaoServiceDecoder),
        resource: D.string,
        action: resourceActionDecoder,
        roles: D.oneOf(D.array(roleDecoder), D.array(D.string)),
    },
});

const roleOnUserDecoder: D.Decoder<BimDto.RoleOnUser> = validateStrictDecoder<BimDto.RoleOnUser>()(
    D.object({
        required: {
            roleId: D.string,
            name: D.string,
        },
    }),
);

export const userDecoder: D.Decoder<BimDto.User> = validateStrictDecoder<BimDto.User>()(
    D.object({
        required: {
            userId: D.string,
            enabled: D.boolean,
            auth0UserId: D.string,
            auth0UserEmail: D.string,
            roles: D.array(roleOnUserDecoder),
        },
        optional: {
            firstName: D.nullable(D.string),
            email: D.nullable(D.string),
            lastName: D.nullable(D.string),
        },
    }),
);

const selfTenantsOptionDecoder: D.Decoder<BimDto.AppUserTenantOption> = D.object({
    required: {
        tenantId: D.string,
        name: D.string,
        status: tenantStatusDecoder,
    },
});

const pageIdDecoder: D.Decoder<Page> = D.literalUnion(
    ...Object.values(SettingsPage),
    ...Object.values(CfeConnectPage),
    ...Object.values(CfeInventoryPage),
    ...Object.values(CfeDiagnosticPage),
    ...Object.values(UploadsPage),
    ...Object.values(AuthPage),
);

const tenantDisplayDecoder: D.Decoder<BimDto.TenantDisplay> = D.object({
    required: {
        homepage: D.nullable(pageIdDecoder),
        visibleNavigationPageIds: D.array(pageIdDecoder),
    },
});

const activeTenantDecoder: D.Decoder<BimDto.ActiveTenant> = D.object({
    required: {
        tenantId: D.string,
        name: D.string,
        status: tenantStatusDecoder,
        services: D.array(flexidaoServiceDecoder),
        displaySettings: tenantDisplayDecoder,
    },
});

export const selfDecoder: D.Decoder<BimDto.AppUser> = validateStrictDecoder<BimDto.AppUser>()(
    D.object({
        required: {
            userId: D.string,
            enabled: D.boolean,
            auth0UserId: D.string,
            auth0UserEmail: D.string,
            activeTenant: activeTenantDecoder,
            tenants: D.array(selfTenantsOptionDecoder),
            roleIds: D.array(D.string),
            version: D.string,
        },
        optional: {
            firstName: D.nullable(D.string),
            email: D.nullable(D.string),
            lastName: D.nullable(D.string),
        },
    }),
);

export const getUsersDecoder: D.Decoder<BimDto.GetUsersResponse> =
    validateStrictDecoder<BimDto.GetUsersResponse>()(
        D.object({
            required: {
                totalUsers: D.number,
                users: D.array(userDecoder),
            },
        }),
    );

const authorizeUserResponseDecoder: D.Decoder<BimDto.UserAuthorizeActionResponse> =
    validateStrictDecoder<BimDto.UserAuthorizeActionResponse>()(
        D.object({
            optional: {
                tenantId: D.string,
                roles: D.array(D.string),
            },
            required: {
                identity: D.literal("userId"),
                id: D.string,
                user: D.object({
                    optional: {
                        firstName: D.nullable(D.string),
                        lastName: D.nullable(D.string),
                    },
                    required: {
                        auth0UserEmail: D.string,
                    },
                }),
                permissionMatches: D.array(
                    D.object({
                        optional: {
                            permissionId: D.string,
                            oAuthScope: D.string,
                        },
                    }),
                ),
            },
        }),
    );

const authorizeServiceAccountResponseDecoder: D.Decoder<BimDto.ServiceAccountAuthorizeActionResponse> =
    validateStrictDecoder<BimDto.ServiceAccountAuthorizeActionResponse>()(
        D.object({
            optional: {
                tenantId: D.string,
                roles: D.array(D.string),
            },
            required: {
                identity: D.literal("serviceAccountId"),
                id: D.string,
                permissionMatches: D.array(
                    D.object({
                        optional: {
                            permissionId: D.string,
                            oAuthScope: D.string,
                        },
                    }),
                ),
            },
        }),
    );

export const postAdminAuthorizeDecoder: D.Decoder<BimDto.AuthorizeActionResponse> =
    validateStrictDecoder<BimDto.AuthorizeActionResponse>()(
        D.oneOf(authorizeUserResponseDecoder, authorizeServiceAccountResponseDecoder),
    );

export const energySourcesArrayDecoder: D.Decoder<Array<BimDto.EnergySource>> = D.array(
    contractEnergySourceDecoder,
);

export const countryDecoder: D.Decoder<BimDto.Country> = D.object({
    required: {
        countryId: D.string,
        name: D.string,
        enabled: D.boolean,
    },
});

export const regionDecoder: D.Decoder<BimDto.Region> = D.object({
    required: {
        regionId: D.string,
        name: D.string,
    },
});

export const biddingZoneDecoder: D.Decoder<BimDto.BiddingZone> = D.object({
    required: {
        zoneId: D.string,
        name: D.string,
        country: countryDecoder,
        timezones: D.array(D.string), //timeZoneDecoder,
        enabled: D.boolean,
        dataAccess: dataAccessDecoder,
        region: regionDecoder,
    },
});

export const siteDecoder: D.Decoder<BimDto.Site> = validateStrictDecoder<BimDto.Site>()(
    D.object({
        required: {
            siteId: D.string,
            name: D.string,
            tenantId: D.string,
            siteType: siteTypeDecoder,
            country: countryDecoder,
            biddingZone: biddingZoneDecoder,
            timezone: D.string, //timeZoneDecoder,
            consumptionSiteGroup: D.nullable(
                D.object({
                    required: {
                        consumptionSiteGroupId: D.string,
                        name: D.string,
                    },
                }),
            ),
            installedCapacityW: D.nullable(D.number),
        },
        optional: {
            energySource: D.nullable(energySourceDecoder),
            onSite: D.nullable(D.boolean),
            isQuarterHourGranularity: D.nullable(D.boolean),
        },
    }),
);

export const sitesArrayDecoder: D.Decoder<Array<BimDto.Site>> = D.array(siteDecoder);

export const getAllSitesDecoder: D.Decoder<BimDto.GetSitesByTenantIdResponse> = D.object({
    required: {
        totalSites: D.number,
        sites: sitesArrayDecoder,
    },
});

const countryOptionDecoder: D.Decoder<BimDto.CountryOption> = D.object({
    required: {
        countryId: D.string,
        name: D.string,
    },
});

const biddingZoneOptionDecoder: D.Decoder<BimDto.BiddingZoneOption> = D.object({
    required: {
        zoneId: D.string,
        name: D.string,
    },
});

export const getSitesFiltersDecoder: D.Decoder<BimDto.GetSitesFiltersResponse> = D.object({
    required: {
        countries: D.array(countryOptionDecoder),
        biddingZones: D.array(biddingZoneOptionDecoder),
        siteTypes: D.array(siteTypeDecoder),
    },
});

export const biddingZoneInCountryDecoder: D.Decoder<BimDto.BiddingZoneInCountry> = D.object({
    required: {
        zoneId: D.string,
        name: D.string,
        enabled: D.boolean,
        timezones: D.array(D.string),
    },
});

export const countryWithZonesDecoder: D.Decoder<BimDto.CountryWithZones> = D.object({
    required: {
        countryId: D.string,
        name: D.string,
        enabled: D.boolean,
        biddingZones: D.array(biddingZoneInCountryDecoder),
    },
});

export const countriesWithZonesArrayDecoder: D.Decoder<Array<BimDto.CountryWithZones>> =
    D.array(countryWithZonesDecoder);

const singleSitePayloadDecoder: D.Decoder<[string]> = D.array(D.string).andThen((sites) => {
    if (sites.length > 1 || !sites[0]) {
        throw new D.DecoderError("It must only contain a single site.");
    }
    // Single item arrays are treated as consts
    return [sites[0]] as const;
});

const singleSiteDecoder: D.Decoder<[BimDto.Site]> = D.array(siteDecoder).andThen((sites) => {
    if (sites.length > 1 || !sites[0]) {
        throw new D.DecoderError("It must only contain a single site.");
    }
    // Single item arrays are treated as consts
    return [sites[0]] as const;
});

//Contracts

export const regionsArrayDecoder: D.Decoder<Array<BimDto.Region>> = D.array(regionDecoder);

export const matchingLogicTypeDecoder: D.Decoder<MatchingLogic> = D.literalUnion(
    MatchingLogic.PayAsConsumed,
    MatchingLogic.PayAsProduced,
);

export const invoicingLevelDecoder: D.Decoder<InvoicingLevel> = D.literalUnion(
    InvoicingLevel.ContractLevel,
    InvoicingLevel.SiteLevel,
);

export const vppaContractDecoder: D.Decoder<BimDto.VppaContract> = D.object({
    required: {
        contractType: D.literalUnion(ContractType.VPPA),
        contractId: D.string,
        tenantId: D.string,
        name: D.string,
        startTimeLocal: isoTimestampDecoder,
        endTimeLocal: isoTimestampDecoder,
        matchingLogic: matchingLogicTypeDecoder,
        percentageCoverage: D.number.andThen((percentage) => {
            if (percentage < 0 || percentage > 100) {
                throw new D.DecoderError(
                    `Percentage coverage must be between 0 and 100, but was ${percentage}`,
                );
            }
            return percentage;
        }),
        compliantCFE: D.boolean,
        region: D.object({
            required: {
                regionId: D.string,
                name: D.string,
            },
        }),
        consumptionSites: sitesArrayDecoder,
        productionSites: sitesArrayDecoder,
        currency: D.string,
        invoicingLevel: invoicingLevelDecoder,
    },
    optional: {
        contractDetails: D.nullable(D.string),
    },
});

export const directPpaContractDecoder: D.Decoder<BimDto.DirectPpaContract> = D.object({
    required: {
        contractType: D.literalUnion(ContractType.DirectPPA),
        contractId: D.string,
        tenantId: D.string,
        name: D.string,
        startTimeLocal: isoTimestampDecoder,
        endTimeLocal: isoTimestampDecoder,
        matchingLogic: matchingLogicTypeDecoder,
        percentageCoverage: D.number.andThen((percentage) => {
            if (percentage < 0 || percentage > 100) {
                throw new D.DecoderError(
                    `Percentage coverage must be between 0 and 100, but was ${percentage}`,
                );
            }
            return percentage;
        }),
        compliantCFE: D.boolean,
        region: D.object({
            required: {
                regionId: D.string,
                name: D.string,
            },
        }),
        productionSites: singleSiteDecoder,
        consumptionSites: singleSiteDecoder,
        currency: D.string,
        invoicingLevel: invoicingLevelDecoder,
    },
    optional: {
        contractDetails: D.nullable(D.string),
    },
});

export const ppaContractDecoder: D.Decoder<BimDto.PpaContract> = D.object({
    required: {
        contractType: D.literalUnion(ContractType.PPA),
        contractId: D.string,
        tenantId: D.string,
        name: D.string,
        startTimeLocal: isoTimestampDecoder,
        endTimeLocal: isoTimestampDecoder,
        matchingLogic: matchingLogicTypeDecoder,
        percentageCoverage: D.number.andThen((percentage) => {
            if (percentage < 0 || percentage > 100) {
                throw new D.DecoderError(
                    `Percentage coverage must be between 0 and 100, but was ${percentage}`,
                );
            }
            return percentage;
        }),
        compliantCFE: D.boolean,
        region: D.object({
            required: {
                regionId: D.string,
                name: D.string,
            },
        }),
        consumptionSites: sitesArrayDecoder,
        productionSites: sitesArrayDecoder,
        currency: D.string,
        invoicingLevel: invoicingLevelDecoder,
    },
    optional: {
        contractDetails: D.nullable(D.string),
    },
});

export const greenTariffContractDecoder: D.Decoder<BimDto.GreenTariffContract> = D.object({
    required: {
        contractType: D.literalUnion(ContractType.GreenTariff),
        contractId: D.string,
        tenantId: D.string,
        name: D.string,
        startTimeLocal: isoTimestampDecoder,
        endTimeLocal: isoTimestampDecoder,
        matchingLogic: D.literalUnion(MatchingLogic.PayAsConsumed),
        percentageCoverage: D.number.andThen((percentage) => {
            if (percentage < 0 || percentage > 100) {
                throw new D.DecoderError(
                    `Percentage coverage must be between 0 and 100, but was ${percentage}`,
                );
            }
            return percentage;
        }),
        compliantCFE: D.boolean,
        region: D.object({
            required: {
                regionId: D.string,
                name: D.string,
            },
        }),
        consumptionSites: sitesArrayDecoder,
        currency: D.string,
        invoicingLevel: invoicingLevelDecoder,
    },
    optional: {
        productionSites: sitesArrayDecoder,
        contractDetails: D.nullable(D.string),
    },
});

const unbundledContractDecoder: D.Decoder<BimDto.UnbundledContract> =
    validateStrictDecoder<BimDto.UnbundledContract>()(
        D.object({
            required: {
                unbundledContractId: D.string,
                tenantId: D.string,
                name: D.string,
                startTimeLocal: isoTimestampDecoder,
                endTimeLocal: isoTimestampDecoder,
                counterparty: D.string,
                updated: isoTimestampDecoder,
                deleted: D.nullable(isoTimestampDecoder),
            },
        }),
    );

export const getUnbundledContractsDecoder: D.Decoder<Array<BimDto.UnbundledContract>> =
    validateStrictDecoder<Array<BimDto.UnbundledContract>>()(D.array(unbundledContractDecoder));

const unbundledTransactionDecoder: D.Decoder<BimDto.UnbundledTransaction> = D.object({
    required: {
        contractType: D.literal(ContractType.UnbundledTransaction),
        contractId: D.string,
        tenantId: D.string,
        name: D.string,
        startTimeLocal: isoTimestampDecoder,
        endTimeLocal: isoTimestampDecoder,
        region: D.object({
            required: {
                regionId: D.string,
                name: D.string,
            },
        }),
        currency: D.string,
        price: D.number,
        volumeMWh: D.number,
        consumptionSites: sitesArrayDecoder,
        updated: isoTimestampDecoder,
        deleted: D.nullable(isoTimestampDecoder),
        unbundledContract: unbundledContractDecoder,
    },
    optional: {
        contractDetails: D.nullable(D.string),
        startProductionLocal: isoTimestampDecoder,
        endProductionLocal: isoTimestampDecoder,
        productionCountries: D.array(D.string),
        energySources: D.array(energySourceDecoder),
    },
});

export const contractDecoder: D.Decoder<BimDto.Contract> = validateStrictDecoder<BimDto.Contract>()(
    D.oneOf(
        ppaContractDecoder,
        unbundledTransactionDecoder,
        vppaContractDecoder,
        directPpaContractDecoder,
        greenTariffContractDecoder,
    ),
);

export const greenTariffContractPayloadDecoder: D.Decoder<BimDto.GreenTariffContractPayload> =
    D.object({
        required: {
            contractType: D.literalUnion(ContractType.GreenTariff),
            name: D.string,
            startTimeLocal: isoTimestampDecoder,
            endTimeLocal: isoTimestampDecoder,
            matchingLogic: D.literalUnion(MatchingLogic.PayAsConsumed),
            percentageCoverage: D.number.andThen((percentage) => {
                if (percentage < 0 || percentage > 100) {
                    throw new D.DecoderError(
                        `Percentage coverage must be between 0 and 100, but was ${percentage}`,
                    );
                }
                return percentage;
            }),
            compliantCFE: D.boolean,
            region: D.string,
            productionSites: D.nullable(D.array(D.string)),
            consumptionSites: D.array(D.string),
            contractDetails: D.nullable(D.string),
            currency: D.string,
            invoicingLevel: invoicingLevelDecoder,
        },
    });

export const vppaContractPayloadDecoder: D.Decoder<BimDto.VppaContractPayload> = D.object({
    required: {
        contractType: D.literalUnion(ContractType.VPPA),
        name: D.string,
        startTimeLocal: isoTimestampDecoder,
        endTimeLocal: isoTimestampDecoder,
        matchingLogic: matchingLogicTypeDecoder,
        percentageCoverage: D.number.andThen((percentage) => {
            if (percentage < 0 || percentage > 100) {
                throw new D.DecoderError(
                    `Percentage coverage must be between 0 and 100, but was ${percentage}`,
                );
            }
            return percentage;
        }),
        compliantCFE: D.boolean,
        region: D.string,
        productionSites: D.array(D.string).andThen((productionSites) => {
            if (productionSites.length === 0) {
                throw new D.DecoderError(`Production sites must be at least one`);
            }
            return productionSites;
        }),
        consumptionSites: D.array(D.string),
        contractDetails: D.nullable(D.string),
        currency: D.string,
        invoicingLevel: invoicingLevelDecoder,
    },
});

export const ppaContractPayloadDecoder: D.Decoder<BimDto.PpaContractPayload> = D.object({
    required: {
        contractType: D.literalUnion(ContractType.PPA),
        name: D.string,
        startTimeLocal: isoTimestampDecoder,
        endTimeLocal: isoTimestampDecoder,
        matchingLogic: matchingLogicTypeDecoder,
        percentageCoverage: D.number.andThen((percentage) => {
            if (percentage < 0 || percentage > 100) {
                throw new D.DecoderError(
                    `Percentage coverage must be between 0 and 100, but was ${percentage}`,
                );
            }
            return percentage;
        }),

        compliantCFE: D.boolean,
        region: D.string,
        productionSites: D.array(D.string).andThen((productionSites) => {
            if (productionSites.length === 0) {
                throw new D.DecoderError(`Production sites must be at least one`);
            }
            return productionSites;
        }),
        consumptionSites: D.array(D.string),
        contractDetails: D.nullable(D.string),
        currency: D.string,
        invoicingLevel: invoicingLevelDecoder,
    },
});

export const directPpaContractPayloadDecoder: D.Decoder<BimDto.DirectPpaContractPayload> = D.object(
    {
        required: {
            contractType: D.literalUnion(ContractType.DirectPPA),
            name: D.string,
            startTimeLocal: isoTimestampDecoder,
            endTimeLocal: isoTimestampDecoder,
            matchingLogic: matchingLogicTypeDecoder,
            percentageCoverage: D.number.andThen((percentage) => {
                if (percentage < 0 || percentage > 100) {
                    throw new D.DecoderError(
                        `Percentage coverage must be between 0 and 100, but was ${percentage}`,
                    );
                }
                return percentage;
            }),
            compliantCFE: D.boolean,
            region: D.string,
            productionSites: singleSitePayloadDecoder,
            consumptionSites: singleSitePayloadDecoder,
            contractDetails: D.nullable(D.string),
            currency: D.string,
            invoicingLevel: invoicingLevelDecoder,
        },
    },
);

export const contractSupportingDocumentDecoder: D.Decoder<BimDto.ContractSupportingDocument> =
    validateStrictDecoder<BimDto.ContractSupportingDocument>()(
        D.object({
            required: {
                fileId: D.number,
                name: D.string,
                sizeBytes: D.number,
                uploadDate: isoTimestampDecoder,
            },
        }),
    );

export const contractSupportingDocumentsDecoder: D.Decoder<
    Array<BimDto.ContractSupportingDocument>
> = D.array(contractSupportingDocumentDecoder);

export const unbundledTransactionPayloadDecoder =
    validateStrictDecoder<BimDto.UnbundledTransactionPayload>()(
        D.object({
            required: {
                contractType: D.literal(ContractType.UnbundledTransaction),
                name: D.string,
                startTimeLocal: isoTimestampDecoder,
                endTimeLocal: isoTimestampDecoder,
                region: D.string,
                currency: D.string,
                price: D.number,
                volumeMWh: D.number,
                energySources: D.nullable(D.array(energySourceDecoder)),
                productionCountries: D.nullable(D.array(D.string)),
                endProductionLocal: D.nullable(isoTimestampDecoder),
                startProductionLocal: D.nullable(isoTimestampDecoder),
                contractDetails: D.nullable(D.string),
                consumptionSites: D.array(D.string).andThen<NonEmptyArray<string>>(
                    (consumptionSites): NonEmptyArray<string> => {
                        if (!consumptionSites[0]) {
                            throw new D.DecoderError(`Consumption sites must be at least one`);
                        }
                        return [consumptionSites[0], ...consumptionSites.slice(1)];
                    },
                ),
                unbundledContractId: D.string,
            },
        }),
    );

export const contractPayloadDecoder: D.Decoder<BimDto.ContractPayload> =
    validateStrictDecoder<BimDto.ContractPayload>()(
        D.oneOf(
            vppaContractPayloadDecoder,
            ppaContractPayloadDecoder,
            directPpaContractPayloadDecoder,
            greenTariffContractPayloadDecoder,
            unbundledTransactionPayloadDecoder,
        ),
    );

export const contractsArrayDecoder: D.Decoder<Array<BimDto.Contract>> = D.array(contractDecoder);

export const getAllContractsDecoder: D.Decoder<BimDto.GetContractsResponse> = D.object({
    required: {
        totalContracts: D.number,
        contracts: contractsArrayDecoder,
    },
});

export const getContractsFiltersDecoder: D.Decoder<BimDto.GetContractsFiltersResponse> = D.object({
    required: {
        regions: regionsArrayDecoder,
        contractTypes: D.array(contractTypeDecoder),
        compliantCFE: D.array(D.boolean),
    },
});

export const siteSummaryDecoder: D.Decoder<BimDto.SiteSummary> = D.object({
    required: {
        siteId: D.string,
        siteName: D.string,
        regionId: D.string,
        regionName: D.string,
        countryId: D.string,
        onSite: D.boolean,
        associatedContracts: D.array(D.string),
    },
});

export const regionSummaryDecoder: D.Decoder<BimDto.Region> = D.object({
    required: {
        regionId: D.string,
        name: D.string,
    },
});

export const siteSummaryArrayDecoder: D.Decoder<Array<BimDto.SiteSummary>> =
    D.array(siteSummaryDecoder);

export const regionSummaryArrayDecoder: D.Decoder<Array<BimDto.Region>> =
    D.array(regionSummaryDecoder);

export const getConsumptionSitesSummaryDecoder: D.Decoder<BimDto.ConsumptionSitesSummary> =
    D.object({
        required: {
            regions: regionSummaryArrayDecoder,
            consumptionSites: siteSummaryArrayDecoder,
        },
    });

export const baseConsumptionSiteGroupDecoder: D.Decoder<BimDto.BaseConsumptionSiteGroup> = D.object(
    {
        required: {
            consumptionSiteGroupId: D.string,
            tenantId: D.string,
            siteIds: D.array(D.string),
            name: D.string,
        },
    },
);

export const baseConsumptionSiteGroupArrayDecoder: D.Decoder<
    Array<BimDto.BaseConsumptionSiteGroup>
> = D.array(baseConsumptionSiteGroupDecoder);

export const consumptionSiteGroupDetailDecoder: D.Decoder<BimDto.ConsumptionSiteGroupDetail> =
    D.object({
        required: {
            consumptionSiteGroupId: D.string,
            tenantId: D.string,
            sites: D.array(
                D.object({
                    required: {
                        siteId: D.string,
                        name: D.string,
                        countryName: D.string,
                    },
                }),
            ),
            name: D.string,
        },
    });
